import { useContext } from "react";
import { ReactReduxContext } from "react-redux";

import { AppStrings } from "../../../../../utils/constants/AppStrings";

// Resolves the display label for the currency that monthly-challenge rewards are
// paid in. The label comes from the platform ("webappconfig") response stored in
// Redux (`app_currency_config.primary_currency.name`, e.g. "Rebet Cash") — it is
// never hardcoded, so it always follows the operator's configured currency.
//
// Read through ReactReduxContext (not useSelector) so the hook stays safe in
// component tests that render without a Provider; the platform config is set once
// at boot, so a one-shot getState() read is equivalent to subscribing. Falls back
// to AppStrings.PRIMARY_CURRENCY (runtime strings.json) when config is absent.
export const useRewardCurrencyLabel = () => {
  const reduxContext = useContext(ReactReduxContext);
  const configLabel = reduxContext
    ? reduxContext.store.getState()?.config?.appConfig?.app_currency_config
        ?.primary_currency?.name
    : undefined;
  return configLabel || AppStrings.PRIMARY_CURRENCY;
};

// Full name of the secondary/play currency (`secondary_currency.name`, e.g.
// "Rebet Coins") for `play`-denominated rewards (FE-2770). Same
// ReactReduxContext rationale as above; falls back to AppStrings.SECONDARY_CURRENCY
// when the platform config is absent.
export const useRewardSecondaryCurrencyLabel = () => {
  const reduxContext = useContext(ReactReduxContext);
  const configLabel = reduxContext
    ? reduxContext.store.getState()?.config?.appConfig?.app_currency_config
        ?.secondary_currency?.name
    : undefined;
  return configLabel || AppStrings.SECONDARY_CURRENCY;
};

const useCurrencyShortcode = (key, fallback) => {
  const reduxContext = useContext(ReactReduxContext);
  const currency = reduxContext
    ? reduxContext.store.getState()?.config?.appConfig?.app_currency_config?.[
        key
      ]
    : undefined;
  return currency?.shortcode || currency?.name || fallback;
};

// Shortcode variant of the above (`primary_currency.shortcode`, e.g. "RCH") for
// compact table cells (FE-2435: Pick History "Result" amounts). Same
// ReactReduxContext rationale; falls back to the full currency name when the
// platform config has no shortcode.
export const useRewardCurrencyShortcode = () =>
  useCurrencyShortcode("primary_currency", AppStrings.PRIMARY_CURRENCY);

// Secondary-currency shortcode (e.g. "RCN") for amounts wagered in the play
// currency — a Rebets bet's `money_type` can be `rebet_coin` (FE-2436).
export const useSecondaryCurrencyShortcode = () =>
  useCurrencyShortcode("secondary_currency", AppStrings.SECONDARY_CURRENCY);

export default useRewardCurrencyLabel;
